import { useCallback, useEffect, useRef, useState } from "react";
import type { ActionSummary, BuilderApi, OwnerCommand, Workspace } from "../api/contract";

/** Changes that apply straight away and can be taken back from the toast. */
export const UNDOABLE = ["content.delete", "brand.set", "site.set", "draft.restore", "content.add", "collection.reorder", "content.edit", "content.patch-instance", "media.add", "media.replace", "media.crop", "media.bind", "section.hide", "change.undo"] as const;
export type UndoableKind = typeof UNDOABLE[number];

/**
 * Direct content changes apply straight away, so the owner gets a moment to take them back. Only
 * changes that finish while the workspace is open are offered; older ones stay in activity.
 */
export type UndoState =
  | { phase: "idle" }
  | {
    phase: "offered"; actionId: string; fromDraftId: string; kind: UndoableKind;
    /** For a deletion: the piece of content that comes back. */
    subject: { recordId: string; label: string } | null;
    /** It also changed text (from the owner's comments), so Undo brings back the whole earlier version. */
    edited?: boolean;
    atDraftId: string;
  }
  | { phase: "undoing"; actionId: string }
  | { phase: "undone" }
  | { phase: "failed" };

export interface UseUndoOptions {
  api: BuilderApi;
  siteId: string;
  workspace: Workspace | undefined;
  refresh: () => Promise<unknown>;
  /** How long the offer stays up before it tucks away into recent activity. */
  offerMs?: number;
}

const applied = (action: ActionSummary) =>
  (UNDOABLE as readonly string[]).includes(action.kind) && action.state === "succeeded" && Boolean(action.baseDraftId);

export function useUndo({ api, siteId, workspace, refresh, offerMs = 15_000 }: UseUndoOptions) {
  const [state, setState] = useState<UndoState>({ phase: "idle" });
  const known = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!workspace) return;
    // Everything already applied when the workspace first loads is history, not news.
    if (!known.current) {
      known.current = new Set(workspace.actions.filter(applied).map((action) => action.id));
      return;
    }
    const fresh = workspace.actions.find((action) => applied(action) && !known.current!.has(action.id));
    if (!fresh) return;
    known.current.add(fresh.id);
    setState({ phase: "offered", actionId: fresh.id, fromDraftId: fresh.baseDraftId!, kind: fresh.kind as UndoableKind, subject: fresh.subject ?? null, ...(fresh.edited ? { edited: true } : {}), atDraftId: workspace.site.draftId });
  }, [workspace]);

  const offered = state.phase === "offered";
  useEffect(() => {
    if (!offered) return;
    const timer = setTimeout(() => setState({ phase: "idle" }), offerMs);
    return () => clearTimeout(timer);
  }, [offered, offerMs, state]);

  const undo = useCallback(async () => {
    if (state.phase !== "offered" || !workspace) return;
    const { actionId } = state;
    const command: OwnerCommand = state.kind === "draft.restore"
      ? { kind: "draft.restore", fromDraftId: state.fromDraftId }
      : { kind: "change.undo", actionId };
    setState({ phase: "undoing", actionId });
    try {
      const prepared = await api.submitAction(siteId, {
        actionId: `undo-${actionId}`.slice(0, 64),
        baseDraftId: workspace.site.draftId,
        command,
      });
      if (prepared.state === "awaiting-confirmation" && prepared.proposalDigest) {
        await api.confirmAction(siteId, prepared.id, prepared.proposalDigest);
      } else if (prepared.state !== "queued" && prepared.state !== "running" && prepared.state !== "succeeded") {
        throw new Error("Undo was not accepted");
      }
      setState({ phase: "undone" });
      await refresh();
    } catch {
      setState({ phase: "failed" });
    }
  }, [api, siteId, state, workspace, refresh]);

  const dismiss = useCallback(() => setState({ phase: "idle" }), []);

  return { state, undo, dismiss };
}
